import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useProjects } from "../contexts/ProjectContext"
import { useAuth } from "../contexts/AuthContext"
import {
  Globe,
  Plus,
  RefreshCw,
  X,
  AlertCircle,
  Trash2,
  ArrowLeft,
  ExternalLink,
  TrendingUp,
  FileText,
  Settings,
} from "lucide-react"
import api from "../utils/apiClient"

const CompetitorDetail = () => {
  const navigate = useNavigate()
  const { competitorUrl } = useParams()
  const { currentProject } = useProjects()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState("tracking")
  const [autoTrack, setAutoTrack] = useState(false)
  const [autoTrackLoading, setAutoTrackLoading] = useState(false)
  const [bestPages, setBestPages] = useState([])
  const [sitemaps, setSitemaps] = useState([])
  const [discoveredUrls, setDiscoveredUrls] = useState([])
  const [urlFilter, setUrlFilter] = useState("all")
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    hasPrevPage: false,
    hasNextPage: false
  })
  const [newBestPageUrl, setNewBestPageUrl] = useState("")
  const [newSitemapUrl, setNewSitemapUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [stats, setStats] = useState({ urlStats: {}, sitemapsCount: 0, bestPagesCount: 0 })
  const [showAddUrlModal, setShowAddUrlModal] = useState(false)
  const [newDiscoveredUrl, setNewDiscoveredUrl] = useState("")
  const [modalError, setModalError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [competitorId, setCompetitorId] = useState(null)
  const [sitemapLoading, setSitemapLoading] = useState(false)
  const [urlsLoading, setUrlsLoading] = useState(false)
  const [paginationLoading, setPaginationLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [bestPagesLoading, setBestPagesLoading] = useState(false)
  const [competitor, setCompetitor] = useState(null)
  const [dataLoaded, setDataLoaded] = useState({
    details: false,
    tracking: false,
    configuration: false
  });
  const [actionLoading, setActionLoading] = useState({
    bestPageAdd: false,
    sitemapAdd: false,
    bestPageDelete: [],
    sitemapDelete: [],
  })
  const [searchQuery, setSearchQuery] = useState("");

  const decodedUrl = decodeURIComponent(competitorUrl)
  const projectCompetitor = currentProject?.project_competitors?.find((c) => c.url === decodedUrl)

  useEffect(() => {
    if (!user) {
      setError("Not authenticated. Please log in.")
      navigate("/login")
    }
  }, [user, navigate])

  useEffect(() => {
    if (!currentProject?.project_competitors) return

    if (!projectCompetitor) {
      setError("Competitor not found")
      navigate("/dashboard/competitors")
      return
    }

    const loadCompetitorId = async () => {
      try {
        // The competitor ID is available from projectCompetitor
        if (projectCompetitor?.id) {
          setCompetitorId(projectCompetitor.id)
        } else {
          setError("Failed to load competitor data")
        }
      } catch (err) {
        setError("Failed to load competitor data")
      }
    }

    loadCompetitorId()
  }, [currentProject?.project_competitors, decodedUrl, currentProject?.id, projectCompetitor, navigate])

  useEffect(() => {
    const loadDetailsData = async () => {
      if (!competitorId || dataLoaded.details) return;

      setLoading(true);
      try {
        // Load details for stats
        const { data: detailsData } = await api.competitors.getDetails(competitorId);
        setStats(detailsData.stats);

        // Set competitor details if available
        if (detailsData.details) {
          // Use decodedUrl as fallback if name is empty
          const name = detailsData.details.name || decodedUrl;
          // Use decodedUrl as fallback if website is empty
          const website = detailsData.details.website || decodedUrl;

          setCompetitor({
            id: competitorId,
            name,
            website,
            description: detailsData.details.description || '',
            auto_track: detailsData.details.auto_track || false
          });

          // Set auto track state from details
          setAutoTrack(detailsData.details.auto_track || false);
        }

        // Mark details as loaded
        setDataLoaded(prev => ({ ...prev, details: true }));
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Failed to load competitor data");
      } finally {
        setLoading(false);
      }
    };

    loadDetailsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitorId]); // Only run when competitorId changes

  useEffect(() => {
    const loadTabData = async () => {
      if (!competitorId) return;

      try {
        // Only load URLs data for tracking tab if not already loaded
        if (activeTab === "tracking" && !dataLoaded.tracking) {
          await loadUrls(competitorId, 1);
          setDataLoaded(prev => ({ ...prev, tracking: true }));
        }

        // Only load sitemaps and best pages data for configuration tab if not already loaded
        if (activeTab === "configuration" && !dataLoaded.configuration) {
          setSitemapLoading(true);
          setBestPagesLoading(true);

          try {
            const { data: sitemapsData } = await api.competitors.getSitemaps(competitorId);
            setSitemaps(sitemapsData);
          } finally {
            setSitemapLoading(false);
          }

          try {
            const { data: bestPagesData } = await api.competitors.getBestPages(competitorId);
            setBestPages(bestPagesData);
          } finally {
            setBestPagesLoading(false);
          }

          setDataLoaded(prev => ({ ...prev, configuration: true }));
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Failed to load tab data");
      }
    };

    loadTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitorId, activeTab]); // Run when competitorId or activeTab changes

  useEffect(() => {
    if (competitorId && activeTab === "tracking" && dataLoaded.tracking && urlFilter) {
      loadUrls(competitorId, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitorId, urlFilter]); // Only run when filter changes

  useEffect(() => {
    if (competitorId && activeTab === "tracking" && dataLoaded.tracking) {
      // Reset to first page when search query changes
      loadUrls(competitorId, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitorId, urlFilter, searchQuery]); // Add searchQuery as a dependency

  const loadUrls = async (id = competitorId, requestedPage = currentPage) => {
    if (!id) return

    const page = Math.max(1, parseInt(requestedPage))

    if (page === 1) {
      setUrlsLoading(true)
    } else {
      setPaginationLoading(true)
    }

    try {
      // Include the search query parameter if it exists
      const { data } = await api.competitors.getUrls(
        id,
        page,
        pagination.limit,
        urlFilter !== "all" ? urlFilter : undefined,
        searchQuery.trim() || undefined
      )

      if (!data?.data || !Array.isArray(data.data)) {
        throw new Error('Invalid response format from server')
      }

      const processedUrls = data.data.map(url => {
        if (url.status === 'generated' && !url.content_id && url.generated_content?.id) {
          return { ...url, content_id: url.generated_content.id }
        }
        return url
      })

      setDiscoveredUrls(processedUrls)
      setPagination(data.pagination)
      setCurrentPage(page)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load URLs")
      setDiscoveredUrls([])
      setPagination({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
        hasPrevPage: false,
        hasNextPage: false
      })
    } finally {
      setUrlsLoading(false)
      setPaginationLoading(false)
    }
  }

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page)
      loadUrls(competitorId, page)
    }
  }

  const validateAndFormatUrl = (url) => {
    try {
      // Prepend https:// if no protocol is specified
      if (!/^https?:\/\//i.test(url)) {
        url = `https://${url}`;
      }

      // Check if URL is valid
      new URL(url);
      return url;
    } catch (e) {
      return null;
    }
  };

  const handleAddUrl = async (type) => {
    const url = type === "page" ? newBestPageUrl : newSitemapUrl;
    if (!url.trim() || !competitor?.id) {
      setError("Missing URL or competitor ID");
      return;
    }

    const validatedUrl = validateAndFormatUrl(url.trim());
    if (!validatedUrl) {
      setError("Please enter a valid URL");
      return;
    }

    setError("");
    // Set loading for the specific add button
    setActionLoading(prev => ({
      ...prev,
      [type === "page" ? "bestPageAdd" : "sitemapAdd"]: true
    }));

    try {
      if (type === "page") {
        // Create a temporary ID for optimistic UI update
        const tempId = `temp-${Date.now()}`;

        // Optimistically add to UI
        const newPage = {
          id: tempId,
          url: validatedUrl,
          competitor_id: competitor.id
        };
        setBestPages(currentPages => [...currentPages, newPage]);
        setNewBestPageUrl("");

        // Make API call
        const response = await api.competitors.addBestPage(competitor.id, validatedUrl);

        // Replace temp entry with actual data from server
        if (response.data) {
          setBestPages(currentPages =>
            currentPages.map(page =>
              page.id === tempId ? response.data : page
            )
          );

          // Update stats locally
          setStats(prevStats => ({
            ...prevStats,
            bestPagesCount: prevStats.bestPagesCount + 1
          }));
        }
      } else {
        // Create a temporary ID for optimistic UI update
        const tempId = `temp-${Date.now()}`;

        // Optimistically add to UI
        const newSitemap = {
          id: tempId,
          url: validatedUrl,
          competitor_id: competitor.id
        };
        setSitemaps(currentSitemaps => [...currentSitemaps, newSitemap]);
        setNewSitemapUrl("");

        // Make API call
        const response = await api.competitors.addSitemap(competitor.id, validatedUrl);

        // Replace temp entry with actual data from server
        if (response.data) {
          setSitemaps(currentSitemaps =>
            currentSitemaps.map(sitemap =>
              sitemap.id === tempId ? response.data : sitemap
            )
          );

          // Update stats locally
          setStats(prevStats => ({
            ...prevStats,
            sitemapsCount: prevStats.sitemapsCount + 1
          }));
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to add URL");

      // Revert optimistic UI update on error
      if (type === "page") {
        setBestPages(currentPages =>
          currentPages.filter(page => !page.id.toString().startsWith('temp-'))
        );
      } else {
        setSitemaps(currentSitemaps =>
          currentSitemaps.filter(sitemap => !sitemap.id.toString().startsWith('temp-'))
        );
      }
    } finally {
      // Clear loading for the specific add button
      setActionLoading(prev => ({
        ...prev,
        [type === "page" ? "bestPageAdd" : "sitemapAdd"]: false
      }));
    }
  };

  const handleAutoFetchSitemaps = async () => {
    if (!competitorId) {
      setError("Competitor ID not found. Please try refreshing the page.")
      return
    }

    setSitemapLoading(true)
    setError("")
    setSuccessMessage("")

    try {
      const { data } = await api.competitors.autoFetchSitemaps(competitorId)
      setSitemaps(data.sitemaps)

      // Reset the tracking data loaded state since new URLs might have been discovered
      setDataLoaded(prev => ({ ...prev, tracking: false }))

      // Reload URLs
      await loadUrls(competitorId)

      if (data.stats.discovered === 0) {
        setSuccessMessage("No sitemaps were found. You can try adding them manually.")
      } else {
        setSuccessMessage(
          `Found ${data.stats.discovered} sitemap${data.stats.discovered !== 1 ? "s" : ""}, ` +
          `added ${data.stats.new} new one${data.stats.new !== 1 ? "s" : ""}. ` +
          `Discovered ${data.stats.discoveredUrls} URL${data.stats.discoveredUrls !== 1 ? "s" : ""}, ` +
          `added ${data.stats.newUrls} new URL${data.stats.newUrls !== 1 ? "s" : ""} to tracking.`,
        )
      }

      // Update stats without making a separate API call
      if (data.stats.urlStats) {
        setStats(prevStats => ({
          ...prevStats,
          urlStats: data.stats.urlStats
        }))
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || "Failed to auto-fetch sitemaps"
      setError(`Error: ${errorMessage}. Please try again or add sitemaps manually.`)
    } finally {
      setSitemapLoading(false)
    }
  }

  const handleGenerateContent = async (urlId) => {
    const urlIndex = discoveredUrls.findIndex(url => url.id === urlId);
    if (urlIndex === -1) return;

    const updatedUrls = [...discoveredUrls];
    updatedUrls[urlIndex] = {
      ...updatedUrls[urlIndex],
      status: "generating"
    };
    setDiscoveredUrls(updatedUrls);

    setLoading(true);
    setError("");

    try {
      const response = await api.competitors.generateContent(urlId, currentProject?.id);

      if (response.data && (response.data.content?.id || response.data.data?.id)) {
        const contentId = response.data.content?.id || response.data.data?.id;

        const finalUrls = [...updatedUrls];
        finalUrls[urlIndex] = {
          ...finalUrls[urlIndex],
          status: "generated",
          content_id: contentId
        };
        setDiscoveredUrls(finalUrls);

        setStats(prevStats => ({
          ...prevStats,
          urlStats: {
            ...prevStats.urlStats,
            generated: (prevStats.urlStats.generated || 0) + 1,
            pending: Math.max(0, (prevStats.urlStats.pending || 0) - 1),
            generating: Math.max(0, (prevStats.urlStats.generating || 0) - 1)
          },
        }));

        setSuccessMessage("Content generated successfully!");

        await api.competitors.updateUrlStatus(urlId, "generated", contentId);
      } else {
        // Reload URLs without making an extra API call for details
        await loadUrls(competitorId, currentPage);
        setError("Failed to generate content: unexpected response format");
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to generate content");

      const revertedUrls = [...discoveredUrls];
      revertedUrls[urlIndex] = {
        ...revertedUrls[urlIndex],
        status: "pending"
      };
      setDiscoveredUrls(revertedUrls);

      try {
        await api.competitors.updateUrlStatus(urlId, "pending");
      } catch (updateErr) {
        // Silently fail, we've already shown an error message
      }
    } finally {
      setLoading(false);
    }
  }

  const handleAddDiscoveredUrl = async () => {
    if (!newDiscoveredUrl.trim()) {
      setModalError("Please enter a URL");
      return;
    }

    if (!competitor?.id) {
      setModalError("Unable to add URL at this time. Please try refreshing the page.");
      return;
    }

    const validatedUrl = validateAndFormatUrl(newDiscoveredUrl.trim());
    if (!validatedUrl) {
      setModalError("Please enter a valid URL");
      return;
    }

    setLoading(true);
    setModalError("");
    setError("");

    try {
      // Create a temporary object for optimistic UI update
      const tempUrlId = `temp-${Date.now()}`;
      const newUrl = {
        id: tempUrlId,
        url: validatedUrl,
        competitor_id: competitor.id,
        status: "pending",
        discovered_at: new Date().toISOString()
      };

      // Optimistically update UI
      setDiscoveredUrls(currentUrls => [newUrl, ...currentUrls]);

      // Update stats locally
      setStats(prevStats => ({
        ...prevStats,
        urlStats: {
          ...prevStats.urlStats,
          pending: (prevStats.urlStats.pending || 0) + 1
        }
      }));

      // Close modal early for better UX
      setNewDiscoveredUrl("");
      setShowAddUrlModal(false);

      // Make the API call
      await api.competitors.addUrl(competitor.id, validatedUrl);

      // Reset the tracking data loaded state and reload URLs to get the actual entry
      setDataLoaded(prev => ({ ...prev, tracking: false }));
      await loadUrls(competitorId);
    } catch (err) {
      setModalError(err.response?.data?.error || err.message || "Failed to add URL");

      // Revert optimistic UI update on error
      setDiscoveredUrls(currentUrls =>
        currentUrls.filter(url => !url.id.toString().startsWith('temp-'))
      );

      // Revert stats
      setStats(prevStats => ({
        ...prevStats,
        urlStats: {
          ...prevStats.urlStats,
          pending: Math.max(0, (prevStats.urlStats.pending || 0) - 1)
        }
      }));

      // Reopen modal if there was an error
      setShowAddUrlModal(true);
    } finally {
      setLoading(false);
    }
  };

  // This is for the add URL modal
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading) {
      e.preventDefault()
      handleAddDiscoveredUrl()
    }
  };

  // This is for the best page URL input
  const handleBestPageKeyDown = (e) => {
    if (e.key === "Enter" && !actionLoading.bestPageAdd) {
      e.preventDefault()
      handleAddUrl('page')
    }
  };

  // This is for the sitemap URL input
  const handleSitemapKeyDown = (e) => {
    if (e.key === "Enter" && !actionLoading.sitemapAdd) {
      e.preventDefault()
      handleAddUrl('sitemap')
    }
  };

  // This is for the search input
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      // Search is automatically triggered by the useEffect
    }
  };

  const handleDeleteBestPage = async (pageId) => {
    if (!competitor?.id) return;

    // Set loading for this specific page delete
    setActionLoading(prev => ({
      ...prev,
      bestPageDelete: [...prev.bestPageDelete, pageId]
    }));
    setError("");

    // Store the page being deleted for potential rollback
    const pageToDelete = bestPages.find(page => page.id === pageId);

    try {
      // Optimistically update UI
      setBestPages(currentBestPages => currentBestPages.filter(page => page.id !== pageId));

      // Update stats locally
      setStats(prevStats => ({
        ...prevStats,
        bestPagesCount: Math.max(0, prevStats.bestPagesCount - 1)
      }));

      // Make the API call
      await api.competitors.deleteBestPage(competitor.id, pageId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to delete best page");

      // Revert optimistic UI update on error
      if (pageToDelete) {
        setBestPages(currentBestPages => [...currentBestPages, pageToDelete]);

        // Revert stats
        setStats(prevStats => ({
          ...prevStats,
          bestPagesCount: prevStats.bestPagesCount + 1
        }));
      }
    } finally {
      // Clear loading for this specific page delete
      setActionLoading(prev => ({
        ...prev,
        bestPageDelete: prev.bestPageDelete.filter(id => id !== pageId)
      }));
    }
  };

  const handleDeleteSitemap = async (sitemapId) => {
    if (!competitor?.id) return;

    // Set loading for this specific sitemap delete
    setActionLoading(prev => ({
      ...prev,
      sitemapDelete: [...prev.sitemapDelete, sitemapId]
    }));
    setError("");

    // Store the sitemap being deleted for potential rollback
    const sitemapToDelete = sitemaps.find(sitemap => sitemap.id === sitemapId);

    try {
      // Optimistically update UI
      setSitemaps(currentSitemaps => currentSitemaps.filter(sitemap => sitemap.id !== sitemapId));

      // Update stats locally
      setStats(prevStats => ({
        ...prevStats,
        sitemapsCount: Math.max(0, prevStats.sitemapsCount - 1)
      }));

      // Make the API call
      await api.competitors.deleteSitemap(competitor.id, sitemapId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to delete sitemap");

      // Revert optimistic UI update on error
      if (sitemapToDelete) {
        setSitemaps(currentSitemaps => [...currentSitemaps, sitemapToDelete]);

        // Revert stats
        setStats(prevStats => ({
          ...prevStats,
          sitemapsCount: prevStats.sitemapsCount + 1
        }));
      }
    } finally {
      // Clear loading for this specific sitemap delete
      setActionLoading(prev => ({
        ...prev,
        sitemapDelete: prev.sitemapDelete.filter(id => id !== sitemapId)
      }));
    }
  };

  // Helper functions to check loading states
  const isBestPageDeleteLoading = (pageId) => actionLoading.bestPageDelete.includes(pageId);
  const isSitemapDeleteLoading = (sitemapId) => actionLoading.sitemapDelete.includes(sitemapId);

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    // Tab data will be loaded by the useEffect if not already loaded
  }

  const handleAutoTrackToggle = async () => {
    if (!competitorId) return;

    setAutoTrackLoading(true);
    setError("");

    try {
      await api.competitors.updateAutoTrack(competitorId, !autoTrack);
      setAutoTrack(!autoTrack);

      // Update the competitor object
      if (competitor) {
        setCompetitor({
          ...competitor,
          auto_track: !autoTrack
        });
      }

      setSuccessMessage(`Auto Track & Generate ${!autoTrack ? 'enabled' : 'disabled'} successfully`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to update Auto Track setting");
    } finally {
      setAutoTrackLoading(false);
    }
  };

  if (!projectCompetitor) {
    return (
      <div className="flex-1 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-6 xl:px-8 2xl:px-12 py-12">
          <div className="text-center">
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg w-fit mx-auto mb-4">
                <Globe className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Competitor Not Found</h2>
              <p className="text-sm text-gray-500 mb-4">
                The competitor you're looking for doesn't exist or you don't have access to it.
              </p>
              <button
                onClick={() => navigate("/dashboard/competitors")}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 text-sm font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Competitors
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="xl:px-8 2xl:px-12">
        <div className="py-6">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate("/dashboard/competitors")}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-3 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Competitors
            </button>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg">
                <Globe className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{decodedUrl}</h1>
                <p className="text-sm text-gray-600 mt-0.5">Competitor Analysis</p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                <p className="text-red-800 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Auto Track & Generate Toggle */}
          <div className="mb-6">
            <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-4 shadow-sm border border-primary-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary-100 rounded-full mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Auto Track & Generate</h3>
                    <p className="text-sm text-gray-600">
                      Automatically discover and generate content from competitor URLs.
                      {autoTrack ?
                        <span className="text-primary-700 font-medium"> Currently enabled.</span> :
                        <span className="text-gray-500"> Currently disabled.</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">{autoTrack ? 'Enabled' : 'Disabled'}</span>
                    {autoTrackLoading ? (
                      <div className="w-14 h-7 bg-gray-200 rounded-full flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoTrack}
                          onChange={handleAutoTrackToggle}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-600 shadow-sm"></div>
                      </label>
                    )}
                  </div>
                </div>
              </div>
              {successMessage && (
                <div className="mt-3 p-2 bg-green-100 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <p className="text-green-800 text-sm font-medium">{successMessage}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="bg-white rounded-lg p-1 shadow-sm border border-gray-200">
              <div className="flex gap-1">
                <button
                  onClick={() => handleTabChange("tracking")}
                  className={`flex-1 px-4 py-2 rounded-md font-medium transition-all duration-200 text-sm ${activeTab === "tracking"
                    ? "bg-primary-600 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Tracking
                  </div>
                </button>
                <button
                  onClick={() => handleTabChange("configuration")}
                  className={`flex-1 px-4 py-2 rounded-md font-medium transition-all duration-200 text-sm ${activeTab === "configuration"
                    ? "bg-primary-600 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Settings className="w-4 h-4" />
                    Configuration
                  </div>
                </button>
              </div>
            </div>
          </div>

          {activeTab === "tracking" ? (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-1">{stats.urlStats.pending || 0}</div>
                      <div className="text-blue-700 font-medium text-sm">Discovered URLs</div>
                      <div className="text-blue-600 text-xs mt-0.5">Ready for analysis</div>
                    </div>
                    <div className="p-2 bg-blue-200 rounded-lg">
                      <Globe className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-green-600 mb-1">{stats.urlStats.generated || 0}</div>
                      <div className="text-green-700 font-medium text-sm">Articles Generated</div>
                      <div className="text-green-600 text-xs mt-0.5">Content created</div>
                    </div>
                    <div className="p-2 bg-green-200 rounded-lg">
                      <FileText className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* URLs Table */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex flex-col space-y-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Discovered URLs</h2>
                      <p className="text-sm text-gray-600 mt-0.5">Track and generate content from competitor URLs</p>
                    </div>

                    <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between">
                      {/* Left Controls */}
                      <div className="flex flex-wrap gap-3 items-center">
                        {/* Filter Buttons */}
                        <div className="inline-flex items-center p-1 bg-gray-100 rounded-lg shadow-sm">
                          <button
                            onClick={() => setUrlFilter("all")}
                            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${urlFilter === "all"
                              ? "bg-white text-primary-700 shadow-sm"
                              : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                              }`}
                          >
                            All
                          </button>
                          <button
                            onClick={() => setUrlFilter("pending")}
                            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${urlFilter === "pending"
                              ? "bg-white text-primary-700 shadow-sm"
                              : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                              }`}
                          >
                            Pending
                          </button>
                          <button
                            onClick={() => setUrlFilter("generated")}
                            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${urlFilter === "generated"
                              ? "bg-white text-primary-700 shadow-sm"
                              : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                              }`}
                          >
                            Generated
                          </button>
                        </div>

                        {/* Add URL Button */}
                        <button
                          onClick={() => setShowAddUrlModal(true)}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                          disabled={loading}
                        >
                          <Plus className="w-4 h-4" />
                          Add URL
                        </button>
                      </div>

                      {/* Right Controls - Search */}
                      <div className="w-full md:w-1/2 lg:w-2/5 xl:w-1/3">
                        {/* Search Input */}
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="w-4 h-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Search URLs..."
                            className="w-full pl-10 pr-10 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm shadow-sm transition-all duration-200 focus:bg-white"
                          />
                          {searchQuery && (
                            <button
                              onClick={() => setSearchQuery("")}
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto relative">
                  {urlsLoading ? (
                    <div className="flex items-center justify-center p-16">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                        <p className="text-sm text-gray-600 font-medium">Loading URLs...</p>
                      </div>
                    </div>
                  ) : discoveredUrls.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-sm">No URLs found</p>
                      <p className="text-gray-400 text-xs mt-1">Add URLs manually or auto-fetch from sitemaps</p>
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            URL
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Discovered
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {discoveredUrls.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-sm text-gray-900 max-w-md">
                              <div className="flex items-center gap-2">
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:text-primary-600 truncate flex-1"
                                  title={item.url}
                                >
                                  {item.url}
                                </a>
                                <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {new Date(item.discovered_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.status === "generated"
                                  ? "bg-green-100 text-green-800"
                                  : item.status === "generating"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                  }`}
                              >
                                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {item.status === "generating" ? (
                                <div className="flex items-center space-x-2">
                                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-primary-500 border-t-transparent"></div>
                                  <span className="text-gray-500 text-xs">Generating...</span>
                                </div>
                              ) : item.status === "generated" ? (
                                <button
                                  onClick={() => {
                                    if (item.content_id) {
                                      navigate(`/content/${item.content_id}`);
                                    } else {
                                      setError("Cannot view content: missing content ID");
                                    }
                                  }}
                                  className="px-3 py-1 text-white bg-primary-600 hover:bg-primary-700 rounded-md text-xs font-medium transition-colors"
                                  disabled={!item.content_id}
                                >
                                  View
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleGenerateContent(item.id)}
                                  className="px-3 py-1 text-white bg-primary-600 hover:bg-primary-700 rounded-md text-xs font-medium transition-colors"
                                  disabled={loading}
                                >
                                  Generate
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Pagination */}
                {pagination.total > 0 && discoveredUrls.length > 0 && (
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="flex-1 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)} to{" "}
                          {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {paginationLoading && (
                          <div className="mr-2 flex items-center">
                            <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                            <span className="text-xs text-gray-500">Loading...</span>
                          </div>
                        )}
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={!pagination.hasPrevPage || paginationLoading}
                          className="p-2 text-gray-600 hover:text-gray-900"
                        >
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>

                        {pagination.pageNumbers && pagination.pageNumbers.map((pageNum, idx) => (
                          typeof pageNum === 'number' ? (
                            <button
                              key={`page-${pageNum}`}
                              onClick={() => handlePageChange(pageNum)}
                              disabled={paginationLoading}
                              className={`min-w-[32px] h-8 flex items-center justify-center text-sm font-medium rounded ${pageNum === currentPage
                                ? "bg-blue-600 text-white"
                                : "text-gray-700 hover:bg-gray-100"
                                }`}
                            >
                              {pageNum}
                            </button>
                          ) : (
                            <span key={`ellipsis-${idx}`} className="text-gray-500">...</span>
                          )
                        ))}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={!pagination.hasNextPage || paginationLoading}
                          className="p-2 text-gray-600 hover:text-gray-900"
                        >
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Best Pages */}
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-1">Best Pages</h3>
                  <p className="text-sm text-gray-600">Track specific high-performing pages from this competitor</p>
                </div>

                {/* Add Best Page Input */}
                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={newBestPageUrl}
                      onChange={(e) => setNewBestPageUrl(e.target.value)}
                      onKeyDown={handleBestPageKeyDown}
                      placeholder="Enter URL to track"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    />
                    <button
                      onClick={() => handleAddUrl("page")}
                      disabled={actionLoading.bestPageAdd || !newBestPageUrl.trim()}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-all duration-200 w-24"
                    >
                      {actionLoading.bestPageAdd ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Adding...</span>
                        </>
                      ) : (
                        'Add'
                      )}
                    </button>
                  </div>
                </div>

                {/* Best Pages List */}
                <div className="space-y-2">
                  {bestPagesLoading ? (
                    <div className="flex items-center justify-center p-10">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-2"></div>
                        <p className="text-sm text-gray-600">Loading best pages...</p>
                      </div>
                    </div>
                  ) : bestPages.length === 0 ? (
                    <div className="text-center py-4 text-sm text-gray-500">No best pages added yet</div>
                  ) : (
                    bestPages.map((page) => (
                      <div
                        key={page.id}
                        className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            value={page.url}
                            readOnly
                            className="w-full bg-transparent border-none focus:ring-0 text-gray-900 truncate text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <a
                            href={page.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleDeleteBestPage(page.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors flex items-center justify-center"
                            title="Delete Page"
                            disabled={isBestPageDeleteLoading(page.id)}
                          >
                            {isBestPageDeleteLoading(page.id) ? (
                              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Sitemaps */}
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">Sitemaps</h3>
                    <p className="text-sm text-gray-600">Automatically discover URLs from competitor sitemaps</p>
                  </div>
                  <button
                    onClick={handleAutoFetchSitemaps}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                    disabled={sitemapLoading || !competitorId}
                  >
                    {sitemapLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                        <span>Fetching...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        <span>Auto-fetch</span>
                      </>
                    )}
                  </button>
                </div>

                {sitemapLoading ? (
                  <div className="flex items-center justify-center p-10">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-2"></div>
                      <p className="text-sm text-gray-600">Fetching sitemaps...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {successMessage && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                          <p className="text-green-800 text-sm font-medium">{successMessage}</p>
                        </div>
                      </div>
                    )}

                    {/* Add Sitemap Input */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="url"
                          value={newSitemapUrl}
                          onChange={(e) => setNewSitemapUrl(e.target.value)}
                          onKeyDown={handleSitemapKeyDown}
                          placeholder="Enter sitemap URL"
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        />
                        <button
                          onClick={() => handleAddUrl("sitemap")}
                          disabled={actionLoading.sitemapAdd || !newSitemapUrl.trim()}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-all duration-200 w-24"
                        >
                          {actionLoading.sitemapAdd ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Adding...</span>
                            </>
                          ) : (
                            'Add'
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Sitemaps List */}
                    <div className="space-y-2">
                      {sitemaps.map((sitemap) => (
                        <div
                          key={sitemap.id}
                          className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <input
                              type="text"
                              value={sitemap.url}
                              readOnly
                              className="w-full bg-transparent border-none focus:ring-0 text-gray-900 truncate text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={sitemap.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            {sitemap.last_fetched_at && (
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                {new Date(sitemap.last_fetched_at).toLocaleDateString()}
                              </span>
                            )}
                            <button
                              onClick={() => handleDeleteSitemap(sitemap.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors flex items-center justify-center"
                              title="Delete Sitemap"
                              disabled={isSitemapDeleteLoading(sitemap.id)}
                            >
                              {isSitemapDeleteLoading(sitemap.id) ? (
                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                      {sitemaps.length === 0 && (
                        <div className="text-center py-4 text-sm text-gray-500">No sitemaps added yet</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add URL Modal */}
      {showAddUrlModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add URL to Track</h3>
              <button
                onClick={() => {
                  setShowAddUrlModal(false)
                  setNewDiscoveredUrl("")
                  setModalError("")
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {modalError && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{modalError}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="urlInput" className="block text-sm font-medium text-gray-700 mb-1">
                  Competitor URL
                </label>
                <input
                  id="urlInput"
                  type="text"
                  value={newDiscoveredUrl}
                  onChange={(e) => {
                    setNewDiscoveredUrl(e.target.value)
                    setModalError("")
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="https://example.com/page"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm ${modalError ? "border-red-300" : "border-gray-300"
                    }`}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUrlModal(false)
                    setNewDiscoveredUrl("")
                    setModalError("")
                  }}
                  className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddDiscoveredUrl}
                  disabled={loading}
                  className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add URL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CompetitorDetail
